import type { SortingState } from "@tanstack/vue-table";
import { translatePpeRequestStatus } from "@asap/shared/utils/translate";
import { useTableColumn } from "~/composables/useTableColumn";
import { ppeRequestStatus } from "~/supabase.values";
import type { PpeRequestTableFilters } from "~/components/ppe-request/ppe-request.model";

type PpeRequestTableState = {
  selectedFilters: PpeRequestTableFilters;
  sorting: SortingState;
  totalItems: number;
  loading: boolean;
};

const defaultSorting = (): SortingState => [{ id: "created_at", desc: true }];

// Module scope: filters survive navigation away from the page.
const ppeRequestTableState = reactive<PpeRequestTableState>({
  selectedFilters: {
    search: undefined,
    selectedDate: undefined,
    businessUnitIds: undefined,
    wsmTeamMemberIds: [],
    consultantTeamMemberIds: [],
    pageSize: 20,
    pageIndex: 0,
  },
  sorting: defaultSorting(),
  totalItems: 0,
  loading: false,
});

export const usePpeRequestTable = () => {
  const { t } = useI18n();

  const ppeRequestTabs = {
    items: [
      { label: t("ppeRequests.table.tabs.all"), value: "all" },
      ...ppeRequestStatus.map((status) => ({ label: translatePpeRequestStatus.fr[status], value: status })),
    ],
    defaultValue: "all",
  };

  // Labels key the column preferences saved in localStorage: keep their values stable.
  const talentLabel = t("ppeRequests.table.columns.talent");
  const statusLabel = t("ppeRequests.table.columns.status");
  const priceLabel = t("ppeRequests.table.columns.price");
  const createdAtLabel = t("ppeRequests.table.columns.createdAt");
  const wsmLabel = t("ppeRequests.table.columns.wsm");
  const consultantLabel = t("ppeRequests.table.columns.consultant");

  const { selectableColumns, tableKey, tableColumns, updateColumn } = useTableColumn(
    [
      { label: talentLabel, value: { accessorKey: "talent", header: talentLabel }, blocked: true, selected: true },
      {
        label: statusLabel,
        value: { accessorKey: "status", header: statusLabel, isSortable: true },
        blocked: true,
        selected: true,
      },
      { label: priceLabel, value: { accessorKey: "items", header: priceLabel }, selected: true },
      {
        label: createdAtLabel,
        value: { accessorKey: "created_at", header: createdAtLabel, isSortable: true },
        selected: true,
      },
      { label: wsmLabel, value: { accessorKey: "wsm", header: wsmLabel }, selected: true },
      { label: consultantLabel, value: { accessorKey: "consultant", header: consultantLabel }, selected: true },
    ],
    "ppe-requests-table-columns"
  );

  const resetFilters = () => {
    ppeRequestTableState.selectedFilters.search = undefined;
    ppeRequestTableState.selectedFilters.selectedDate = undefined;
    ppeRequestTableState.selectedFilters.businessUnitIds = undefined;
    ppeRequestTableState.selectedFilters.wsmTeamMemberIds = [];
    ppeRequestTableState.selectedFilters.consultantTeamMemberIds = [];
    ppeRequestTableState.sorting = defaultSorting();
  };

  return {
    ppeRequestTableState,
    ppeRequestTabs,
    selectableColumns,
    tableKey,
    tableColumns,
    updateColumn,
    resetFilters,
  };
};
